import type {
    TransportRequestRelationRef,
    TransportRequestStatusRef,
} from '@/Containers/TransportationSection/Components'
import type { UserLike } from '@/Ship/Helpers/fieldViews'

/** Ключ ресурсу заявки — під ним її відкривають реєстр, канбан, «Рейси», пошук і вкладки CRM. */
export const APPLICATION_RESOURCE = 'Transportation.Application'

/** Ключ ресурсу «Рейсів» — той самий бек, інша вибірка (`SPEC-021` §8.2). */
export const TRIP_RESOURCE = 'Transportation.Trip'

/** Оплати й документи — діти заявки, створюються лише з її картки. */
export const APPLICATION_PAYMENT_RESOURCE = 'Transportation.ApplicationPayment'
export const APPLICATION_DOCUMENT_RESOURCE = 'Transportation.ApplicationDocument'

/** Колекція заявок (`SPEC-021` §6.1). */
export const APPLICATION_REST_URL = '/services/applications'
export const APPLICATION_PAYMENT_REST_URL = '/services/application_payments'
export const APPLICATION_DOCUMENT_REST_URL = '/services/application_documents'

/** Крок 1 двокрокового завантаження файлу заявки (`SPEC-021` §4.6). */
export const APPLICATION_FILE_UPLOAD_URL = '/services/application_files'

/** Довідник статусів заявок (`SPEC-007`): рядок статусів, колонки канбану, смуга картки. */
export const APPLICATION_STATUS_RESOURCE = 'Setting.ListApplicationStatus'

/** Довідник причин програшу заявок — варіанти модалки «Програти». */
export const APPLICATION_LOSS_REASON_RESOURCE = 'Setting.ListApplicationLossReason'

/** Ширина шухляди картки (легасі `ApplicationResource.jsx:371-376`). */
export const APPLICATION_DRAWER_WIDTH = 1200

/** Рядків на сторінку реєстру — як бачив користувач легасі (`ApplicationCrud.jsx:84`). */
export const APPLICATION_PAGE_SIZE = 25

/**
 * Системні коди статусів заявки (`ApplicationStatusCode` беку) у **порядку
 * процесу** — порядок case enum (ДП-14): смуга картки йде за ним, а не за
 * `sorting` довідника, який адміністратор може переставити.
 */
export const APPLICATION_STATUS_CODES = {
    new: 'application-new',
    confirmed: 'application-confirmed',
    waitingDownload: 'application-waiting-download',
    inWay: 'application-in-way',
    documentManagement: 'application-document-management',
    problem: 'application-problem',
    successfully: 'application-successfully',
    loss: 'application-loss',
} as const

export type ApplicationStatusCode = (typeof APPLICATION_STATUS_CODES)[keyof typeof APPLICATION_STATUS_CODES]

/** Кроки смуги статусів: усі, крім «Програш» (`SPEC-021` §8.4). */
export const APPLICATION_STATUS_STEPS: readonly ApplicationStatusCode[] = [
    APPLICATION_STATUS_CODES.new,
    APPLICATION_STATUS_CODES.confirmed,
    APPLICATION_STATUS_CODES.waitingDownload,
    APPLICATION_STATUS_CODES.inWay,
    APPLICATION_STATUS_CODES.documentManagement,
    APPLICATION_STATUS_CODES.problem,
    APPLICATION_STATUS_CODES.successfully,
]

/**
 * Префільтр «Рейсів» за статусом (легасі `TripPage.jsx:7-18`): від
 * підтвердження до завершення, без «Новий» і «Програш».
 */
export const TRIP_STATUS_CODES: readonly ApplicationStatusCode[] = [
    APPLICATION_STATUS_CODES.confirmed,
    APPLICATION_STATUS_CODES.waitingDownload,
    APPLICATION_STATUS_CODES.inWay,
    APPLICATION_STATUS_CODES.documentManagement,
    APPLICATION_STATUS_CODES.problem,
    APPLICATION_STATUS_CODES.successfully,
]

/**
 * `_permissions` заявки з `?permissions=true` (`ApplicationRule`): право й
 * дозволеність переходу з поточного стану вже зведено на беку.
 */
export interface ApplicationPermissions {
    read?: boolean
    update?: boolean
    delete?: boolean
    lose?: boolean
    report_problem?: boolean
    resolve_problem?: boolean
    unload?: boolean
}

/** Запис заявки так, як його віддають картка й колекція (лише те, що читає фронт). */
export interface ApplicationRecord {
    uuid?: string
    '@id'?: string
    id?: number
    title?: string
    status?: TransportRequestStatusRef | null
    client?: TransportRequestRelationRef | null
    carrier?: TransportRequestRelationRef | null
    request?: TransportRequestRelationRef | null
    tender?: TransportRequestRelationRef | null
    manager?: UserLike | null
    responsible?: UserLike | null
    loadingCountries?: TransportRequestRelationRef[] | null
    unloadingCountries?: TransportRequestRelationRef[] | null
    typesCars?: TransportRequestRelationRef[] | null
    loadingAddresses?: string[] | null
    unloadingAddresses?: string[] | null
    dateLoading?: string | null
    dateUnloading?: string | null
    numberVehicle?: string | null
    statusClient?: string | null
    createdAt?: string | null
    countOffers?: number | null
    _permissions?: ApplicationPermissions
}

/** Відповідь колекції `GET /services/applications`. */
export interface ApplicationCollectionResponse {
    items?: ApplicationRecord[]
    meta?: { total?: number }
}

/** Значення картки так, як їх тримає ядрова форма. */
export interface ApplicationValues {
    uuid?: string
    '@id'?: string
    id?: number
    title?: string
    status?: TransportRequestStatusRef | null
    client?: TransportRequestRelationRef | null
    _permissions?: ApplicationPermissions
    [key: string]: unknown
}

/** Те, що компоненти заявки беруть із ядрового `formContext`. */
export interface ApplicationFormContextLike {
    isEditable?: boolean
    values?: ApplicationValues
    errors?: Record<string, string>
    getError?: (path: string) => unknown
    setErrors?: (errors: Record<string, string>) => void
    setInitialValue?: (path: string, value: unknown) => void
    setInitialValues?: (values: Record<string, unknown>) => void
    setStorage?: (key: string, value: unknown) => void
}

/** Те, що кнопки й смуга беруть із ядрового `crudContext` картки. */
export interface ApplicationCrudContextLike {
    resource?: { key?: string; restUrl?: string; fields?: Record<string, unknown> }
    formContext?: ApplicationFormContextLike
    actions?: { reloadData?: () => unknown; edit?: (item: Record<string, unknown>) => unknown }
    listCrudContext?: { listContext?: { actions?: { load?: (force?: boolean) => unknown } } }
}
